/** Per-sandbox quota overrides (S5a; SBX D4 §3, O5, O29): the O5 defaults stay the safety net, and ops
 *  raise them for a sandbox fed by production data. A blank override means the default; clearing one
 *  removes it. Each save is audited (`Tour_Operator_Config_Change` + `sandbox.quotas.updated`) and
 *  re-evaluates a reached quota at once, so raising it lifts "lane paused". */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  QUOTA_KEYS,
  sandboxApi,
  sandboxErrorMessage,
  type QuotaKey,
  type SandboxQuotaOverrides,
  type SandboxQuotas,
} from '@/api/sandbox';
import { useSnackbar } from '@/components/Snackbar';

import { QUOTA_LABEL } from './sandboxWords';

const MAX_QUOTA = 10_000_000;

const overrideField = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= MAX_QUOTA), {
    message: `A whole number from 1 to ${MAX_QUOTA.toLocaleString()}, or blank for the default`,
  });

const QuotasSchema = z.object({
  bookings_per_day: overrideField,
  bookings_total: overrideField,
  bookings_per_payload: overrideField,
  requests_per_minute: overrideField,
  comment: z.string().max(250),
});
type QuotaValues = z.infer<typeof QuotasSchema>;

function seed(q: SandboxQuotas): QuotaValues {
  const v = { comment: '' } as QuotaValues;
  for (const k of QUOTA_KEYS) v[k] = q.overrides[k] != null ? String(q.overrides[k]) : '';
  return v;
}

/** The overrides that changed: a number sets one, null removes it. */
export function quotaChanges(q: SandboxQuotas, v: QuotaValues): SandboxQuotaOverrides {
  const out: SandboxQuotaOverrides = {};
  for (const k of QUOTA_KEYS) {
    const raw = v[k].trim();
    const next = raw === '' ? null : Number(raw);
    const before = q.overrides[k] ?? null;
    if (next !== before) out[k] = next;
  }
  return out;
}

export function QuotasSection({
  topId,
  quotas,
  canManage,
}: {
  topId: number;
  quotas: SandboxQuotas;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const storedKey = JSON.stringify(quotas.overrides);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => seed(quotas), [storedKey]);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<QuotaValues>({ defaultValues: initial, resolver: zodResolver(QuotasSchema) });
  useEffect(() => {
    reset(initial);
  }, [initial, reset]);

  const values = watch();
  const changes = quotaChanges(quotas, values);
  const changed = Object.keys(changes).length;

  const save = useMutation({
    mutationFn: (v: QuotaValues) =>
      sandboxApi.setQuotas(topId, {
        quotas: quotaChanges(quotas, v),
        comment: v.comment.trim() || undefined,
      }),
    onSuccess: () => {
      snackbar.show('Quotas saved.', 'success');
      // The throttle may have lifted with the new limits: refresh the whole sandbox state.
      void qc.invalidateQueries({ queryKey: ['tourOperators', 'sandbox', topId] });
    },
    onError: (e) => snackbar.show(sandboxErrorMessage(e), 'error'),
  });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit((v) => save.mutate(v))}
      sx={{ display: 'grid', gap: 2 }}
    >
      <Table size="small" aria-label="Sandbox quotas" sx={{ maxWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Quota</TableCell>
            <TableCell align="right">Default</TableCell>
            <TableCell sx={{ width: 220 }}>Override</TableCell>
            <TableCell align="right">In force</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {QUOTA_KEYS.map((k: QuotaKey) => (
            <TableRow key={k}>
              <TableCell>{QUOTA_LABEL[k]}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {quotas.defaults[k]?.toLocaleString() ?? '—'}
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  disabled={!canManage}
                  inputProps={{ inputMode: 'numeric', 'aria-label': `${QUOTA_LABEL[k]} override` }}
                  placeholder="Default"
                  error={Boolean(errors[k])}
                  helperText={errors[k]?.message}
                  {...register(k)}
                />
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: quotas.overrides[k] != null ? 600 : 400,
                }}
              >
                {quotas.effective[k]?.toLocaleString() ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canManage && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="Comment"
            placeholder="Why — kept in the audit record"
            inputProps={{ maxLength: 250 }}
            sx={{ flex: 1, maxWidth: 480 }}
            {...register('comment')}
          />
          <Button type="submit" variant="contained" disabled={changed === 0 || save.isPending}>
            {save.isPending
              ? 'Saving…'
              : changed > 0
                ? `Save ${changed} quota${changed === 1 ? '' : 's'}`
                : 'Save quotas'}
          </Button>
          {changed > 0 && (
            <Button color="inherit" disabled={save.isPending} onClick={() => reset(initial)}>
              Discard
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
